import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  Camera,
  ChevronRight,
  Heart,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MapPin,
  Package,
  Settings,
  ShieldCheck,
  Star,
  User,
  UserRound,
} from "lucide-react";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";

import "../../styles/profile.css";

export default function Profile() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  // =========================================================
  // CARGAR USUARIO
  // =========================================================

  useEffect(() => {
    try {
      const authRaw = localStorage.getItem("auth");

      if (!authRaw) {
        navigate("/login", { replace: true });
        return;
      }

      const auth = JSON.parse(authRaw);

      if (!auth?.access) {
        navigate("/login", { replace: true });
        return;
      }

      const storedUser =
        auth?.user ||
        auth?.profile ||
        auth?.data?.user ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem("user"));
          } catch {
            return null;
          }
        })() ||
        null;

      if (!storedUser) {
        navigate("/login", { replace: true });
        return;
      }

      setUser(storedUser);
    } catch (error) {
      console.error("Error cargando perfil:", error);

      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("redirectAfterLogin");

      window.dispatchEvent(new Event("authUpdated"));

      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // =========================================================
  // NOMBRE
  // =========================================================

  const userName = useMemo(() => {
    if (!user) {
      return "USUARIO DEBUTS";
    }

    return (
      user.full_name ||
      user.name ||
      user.username ||
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      "USUARIO DEBUTS"
    );
  }, [user]);

  // =========================================================
  // EMAIL
  // =========================================================

  const userEmail = useMemo(() => {
    if (!user) {
      return "usuario@debutscrop.com";
    }

    return user.email || "usuario@debutscrop.com";
  }, [user]);

  // =========================================================
  // LETRA DEL AVATAR
  // =========================================================

  const firstLetter = useMemo(() => {
    return userName.charAt(0).toUpperCase();
  }, [userName]);

  // =========================================================
  // COMPROBAR ADMIN
  // =========================================================

  const isAdmin = useMemo(() => {
    if (!user) {
      return false;
    }

    return (
      user.role === "admin" ||
      user.role === "administrador" ||
      user.role === "ADMIN" ||
      user.role === "ADMINISTRADOR" ||
      user.is_staff === true ||
      user.is_superuser === true
    );
  }, [user]);

  // =========================================================
  // LOGOUT
  // =========================================================

  const handleLogout = () => {
    try {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      localStorage.removeItem("redirectAfterLogin");

      window.dispatchEvent(new Event("authUpdated"));
    } catch (error) {
      console.error("Error cerrando sesión:", error);
    }

    navigate("/login", { replace: true });
  };

  // =========================================================
  // IR AL ADMINISTRADOR
  // =========================================================

  const goToDashboard = () => {
    navigate("/dashboard");
  };

  // =========================================================
  // VOLVER
  // =========================================================

  const goBack = () => {
    navigate(-1);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* =====================================================
          NAVBAR GLOBAL
      ===================================================== */}

      <Navbar />

      {/* =====================================================
          PROFILE PAGE
      ===================================================== */}

      <main className="profile-page">

        {/* ===================================================
            BACKGROUND
        =================================================== */}

        <div className="profile-background-overlay"></div>

        <div className="profile-bg-glow profile-bg-glow-one"></div>
        <div className="profile-bg-glow profile-bg-glow-two"></div>
        <div className="profile-bg-glow profile-bg-glow-three"></div>

        {/* ===================================================
            PAGE CONTENT
        =================================================== */}

        <div className="profile-page-container">

          {/* =================================================
              PROFILE TITLE
          ================================================= */}

          <section className="profile-title-bar">

            <button
              type="button"
              className="profile-back-button"
              onClick={goBack}
              aria-label="Volver"
            >
              <ArrowLeft size={22} />
            </button>

            <div className="profile-title-content">

              <span className="profile-title-small">
                DEBUTS CROP
              </span>

              <h1>
                MI PERFIL
              </h1>

              <div className="profile-title-line"></div>

            </div>

            <button
              type="button"
              className="profile-settings-button"
              onClick={() => navigate("/settings")}
            >
              <Settings size={19} />

              <span>
                Configuración
              </span>
            </button>

          </section>

          {/* =================================================
              MAIN PROFILE LAYOUT
          ================================================= */}

          <section className="profile-layout">

            {/* ===============================================
                SIDEBAR
            =============================================== */}

            <aside className="profile-sidebar">

              <nav className="profile-sidebar-menu">

                {/* PERFIL */}

                <button
                  type="button"
                  className="profile-sidebar-item active"
                >
                  <span className="profile-sidebar-icon">
                    <User size={20} />
                  </span>

                  <span>
                    Perfil
                  </span>
                </button>

                {/* =================================================
                    ADMINISTRACIÓN
                ================================================= */}

                {isAdmin && (
                  <button
                    type="button"
                    className="profile-sidebar-item profile-admin-item"
                    onClick={goToDashboard}
                  >
                    <span className="profile-sidebar-icon">
                      <LayoutDashboard size={20} />
                    </span>

                    <span>
                      Administración
                    </span>

                    <ChevronRight size={17} />
                  </button>
                )}

                {/* PEDIDOS */}

                <button
                  type="button"
                  className="profile-sidebar-item"
                  onClick={() => navigate("/orders")}
                >
                  <span className="profile-sidebar-icon">
                    <Package size={20} />
                  </span>

                  <span>
                    Mis pedidos
                  </span>

                  <ChevronRight size={17} />
                </button>

                {/* FAVORITOS */}

                <button
                  type="button"
                  className="profile-sidebar-item"
                  onClick={() => navigate("/favorites")}
                >
                  <span className="profile-sidebar-icon">
                    <Heart size={20} />
                  </span>

                  <span>
                    Favoritos
                  </span>

                  <ChevronRight size={17} />
                </button>

                {/* DIRECCIONES */}

                <button
                  type="button"
                  className="profile-sidebar-item"
                  onClick={() => navigate("/addresses")}
                >
                  <span className="profile-sidebar-icon">
                    <MapPin size={20} />
                  </span>

                  <span>
                    Direcciones
                  </span>

                  <ChevronRight size={17} />
                </button>

              </nav>

              <div className="profile-sidebar-divider"></div>

              {/* SEGURIDAD */}

              <div className="profile-sidebar-section-title">
                SEGURIDAD
              </div>

              {/* CAMBIAR CONTRASEÑA */}

              <button
                type="button"
                className="profile-sidebar-item"
                onClick={() => navigate("/change-password")}
              >
                <span className="profile-sidebar-icon">
                  <KeyRound size={20} />
                </span>

                <span>
                  Cambiar contraseña
                </span>

                <ChevronRight size={17} />
              </button>

              {/* =================================================
                  CERRAR SESIÓN
              ================================================= */}

              <button
                type="button"
                className="profile-sidebar-item profile-logout"
                onClick={handleLogout}
              >
                <span className="profile-sidebar-icon">
                  <LogOut size={20} />
                </span>

                <span>
                  Cerrar sesión
                </span>

                <ChevronRight size={17} />
              </button>

              {/* =============================================
                  BRAND SIDEBAR
              ============================================= */}

              <div className="profile-sidebar-brand">

                <div className="profile-sidebar-brand-image">
                  <img
                    src={logo}
                    alt="Debuts Crop"
                  />
                </div>

                <span>
                  TU CULTIVO · TU ESTILO
                </span>

              </div>

            </aside>

            {/* ===============================================
                MAIN CONTENT
            =============================================== */}

            <div className="profile-content">

              {/* =============================================
                  USER CARD
              ============================================= */}

              <section className="profile-user-card">

                <div className="profile-user-background"></div>

                <div className="profile-user-inner">

                  {/* AVATAR */}

                  <div className="profile-avatar-wrapper">

                    <div className="profile-avatar">

                      <span>
                        {firstLetter}
                      </span>

                    </div>

                    <button
                      type="button"
                      className="profile-avatar-camera"
                      aria-label="Cambiar foto"
                    >
                      <Camera size={14} />
                    </button>

                  </div>

                  {/* USER DATA */}

                  <div className="profile-user-info">

                    <div className="profile-user-name-row">

                      <h2>
                        {userName}
                      </h2>

                      <span className="profile-crown">
                        ♛
                      </span>

                    </div>

                    <p className="profile-user-email">
                      {userEmail}
                    </p>

                    <div className="profile-member-badge">
                      MIEMBRO DE DEBUTS CROP
                    </div>

                    <span className="profile-member-date">
                      Desde Abril 2025
                    </span>

                  </div>

                  {/* SLOGAN */}

                  <div className="profile-user-slogan">

                    <strong>
                      TU CULTIVO
                    </strong>

                    <strong>
                      TU ESTILO
                    </strong>

                  </div>

                </div>

              </section>

              {/* =============================================
                  STATS
              ============================================= */}

              <section className="profile-stats">

                {/* PEDIDOS */}

                <button
                  type="button"
                  className="profile-stat-card"
                  onClick={() => navigate("/orders")}
                >
                  <span className="profile-stat-icon">
                    <Package size={24} />
                  </span>

                  <span className="profile-stat-number">
                    12
                  </span>

                  <span className="profile-stat-label">
                    PEDIDOS REALIZADOS
                  </span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>

                {/* FAVORITOS */}

                <button
                  type="button"
                  className="profile-stat-card"
                  onClick={() => navigate("/favorites")}
                >
                  <span className="profile-stat-icon">
                    <Heart size={24} />
                  </span>

                  <span className="profile-stat-number">
                    4
                  </span>

                  <span className="profile-stat-label">
                    FAVORITOS
                  </span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>

                {/* RESEÑAS */}

                <button
                  type="button"
                  className="profile-stat-card"
                >
                  <span className="profile-stat-icon">
                    <Star size={24} />
                  </span>

                  <span className="profile-stat-number">
                    8
                  </span>

                  <span className="profile-stat-label">
                    RESEÑAS
                  </span>

                  <ChevronRight
                    size={19}
                    className="profile-stat-arrow"
                  />
                </button>

              </section>

              {/* =============================================
                  ACCOUNT INFORMATION
              ============================================= */}

              <section className="profile-panel">

                <div className="profile-panel-header">

                  <span className="profile-panel-header-icon">
                    <UserRound size={20} />
                  </span>

                  <h3>
                    Información de la cuenta
                  </h3>

                </div>

                <div className="profile-panel-list">

                  {/* INFORMACIÓN PERSONAL */}

                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/profile/edit")}
                  >
                    <span className="profile-panel-item-icon">
                      <User size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Información personal
                      </strong>

                      <small>
                        Nombre, correo, teléfono, etc.
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                  {/* PEDIDOS */}

                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/orders")}
                  >
                    <span className="profile-panel-item-icon">
                      <Package size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Mis pedidos
                      </strong>

                      <small>
                        Historial y seguimiento
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                  {/* FAVORITOS */}

                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/favorites")}
                  >
                    <span className="profile-panel-item-icon">
                      <Heart size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Favoritos
                      </strong>

                      <small>
                        Productos guardados
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                  {/* DIRECCIONES */}

                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/addresses")}
                  >
                    <span className="profile-panel-item-icon">
                      <MapPin size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Direcciones
                      </strong>

                      <small>
                        Gestiona tus direcciones de envío
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                </div>

              </section>

              {/* =============================================
                  SECURITY
              ============================================= */}

              <section className="profile-panel profile-security-panel">

                <div className="profile-panel-header">

                  <span className="profile-panel-header-icon">
                    <ShieldCheck size={20} />
                  </span>

                  <h3>
                    Seguridad
                  </h3>

                </div>

                <div className="profile-panel-list">

                  {/* CHANGE PASSWORD */}

                  <button
                    type="button"
                    className="profile-panel-item"
                    onClick={() => navigate("/change-password")}
                  >
                    <span className="profile-panel-item-icon">
                      <KeyRound size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Cambiar contraseña
                      </strong>

                      <small>
                        Mantén tu cuenta segura
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                  {/* LOGOUT */}

                  <button
                    type="button"
                    className="profile-panel-item profile-panel-logout"
                    onClick={handleLogout}
                  >
                    <span className="profile-panel-item-icon">
                      <LogOut size={21} />
                    </span>

                    <span className="profile-panel-item-content">

                      <strong>
                        Cerrar sesión
                      </strong>

                      <small>
                        Salir de tu cuenta
                      </small>

                    </span>

                    <ChevronRight size={19} />

                  </button>

                </div>

              </section>

            </div>

          </section>

        </div>

      </main>

      {/* =====================================================
          FOOTER GLOBAL
      ===================================================== */}

      <Footer />
    </>
  );
}